import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { Blueprint, NarrativeThreadEnum, VideoPlan } from 'js/src/client';
import type { StoredTemplate } from 'js/src/data/templates';

export type VideoPlanTemplate = Pick<StoredTemplate, 'id'> &
    Partial<Pick<StoredTemplate, 'display_name' | 'supported_aspect_ratios'>>;

export interface VideoPlanState {
    template: VideoPlanTemplate | null;
    aspectRatio: AspectRatio | null;
    blueprint: Blueprint | null;
    narrativeThread: NarrativeThreadEnum | null;
    voiceSlug: string | null;
}

export interface Incompatibility {
    property: IncompatibleProperty;
    reason: string;
    supportedValues: string[];
}

export type IncompatibleProperty =
    | 'aspectRatio'
    | 'template'
    | 'narrativeThread'
    | 'voice'
    | 'blueprint';

export type PropertyChange =
    | { type: 'template'; newValue: StoredTemplate }
    | { type: 'blueprint'; newValue: Blueprint }
    | { type: 'aspectRatio'; newValue: AspectRatio }
    | { type: 'narrativeThread'; newValue: NarrativeThreadEnum };

function isUnsupported<T>(value: T, supportedValues: readonly T[] | undefined): boolean {
    return !!supportedValues?.length && !supportedValues.includes(value);
}

export function getIncompatiblePropertyLabel(property: IncompatibleProperty): string {
    const labels: Record<IncompatibleProperty, string> = {
        aspectRatio: 'Format',
        template: 'Template',
        voice: 'Voice',
        blueprint: 'Blueprint',
        narrativeThread: 'Voiceover setting',
    };
    return labels[property];
}

export function getVideoPlanIncompatibilities(
    change: PropertyChange,
    currentState: Partial<VideoPlanState>,
): Incompatibility[] {
    switch (change.type) {
        case 'template':
            return getTemplateChangeIncompatibilities(change.newValue, currentState);
        case 'blueprint':
            return getBlueprintChangeIncompatibilities(change.newValue, currentState);
        case 'aspectRatio':
            return getAspectRatioChangeIncompatibilities(change.newValue, currentState);
        case 'narrativeThread':
            return getNarrativeThreadChangeIncompatibilities(change.newValue, currentState);
        default:
            return [];
    }
}

function getTemplateChangeIncompatibilities(
    newTemplate: StoredTemplate,
    currentState: Partial<VideoPlanState>,
): Incompatibility[] {
    const incompatibilities: Incompatibility[] = [];
    const supportedRatios = newTemplate.supported_aspect_ratios ?? [];
    const supportedTemplates = currentState.blueprint?.supported_templates ?? [];

    if (currentState.aspectRatio && isUnsupported(currentState.aspectRatio, supportedRatios)) {
        incompatibilities.push({
            property: 'aspectRatio',
            reason: 'Not supported by the selected template',
            supportedValues: supportedRatios.map((r) =>
                VideoData.getAspectRatioDisplayName(r as AspectRatio),
            ),
        });
    }

    if (currentState.blueprint && isUnsupported(newTemplate.id, supportedTemplates)) {
        incompatibilities.push({
            property: 'blueprint',
            reason: 'Current blueprint does not support this template',
            supportedValues: supportedTemplates,
        });
    }

    return incompatibilities;
}

function getBlueprintChangeIncompatibilities(
    newBlueprint: Blueprint,
    currentState: Partial<VideoPlanState>,
): Incompatibility[] {
    const incompatibilities: Incompatibility[] = [];
    const supportedTemplates = newBlueprint.supported_templates ?? [];

    if (currentState.template && isUnsupported(currentState.template.id, supportedTemplates)) {
        incompatibilities.push({
            property: 'template',
            reason: 'Not supported by the selected blueprint',
            supportedValues: supportedTemplates,
        });
    }

    if (
        currentState.narrativeThread &&
        currentState.narrativeThread !== newBlueprint.narrative_thread
    ) {
        incompatibilities.push({
            property: 'narrativeThread',
            reason: 'Blueprint uses a different narrative thread',
            supportedValues: [newBlueprint.narrative_thread],
        });
    }

    if (currentState.voiceSlug && newBlueprint.narrative_thread === 'text') {
        incompatibilities.push({
            property: 'voice',
            reason: 'Blueprint does not use voiceover',
            supportedValues: [],
        });
    }

    return incompatibilities;
}

function getAspectRatioChangeIncompatibilities(
    newAspectRatio: AspectRatio,
    currentState: Partial<VideoPlanState>,
): Incompatibility[] {
    if (!currentState.template) {
        return [];
    }

    const supportedRatios = currentState.template.supported_aspect_ratios ?? [];

    if (isUnsupported(newAspectRatio, supportedRatios)) {
        return [
            {
                property: 'template',
                reason: 'Current template does not support this aspect ratio',
                supportedValues: supportedRatios.map((r) =>
                    VideoData.getAspectRatioDisplayName(r as AspectRatio),
                ),
            },
        ];
    }

    return [];
}

function getNarrativeThreadChangeIncompatibilities(
    newNarrativeThread: NarrativeThreadEnum,
    currentState: Partial<VideoPlanState>,
): Incompatibility[] {
    if (currentState.blueprint && currentState.blueprint.narrative_thread !== newNarrativeThread) {
        return [
            {
                property: 'blueprint',
                reason: 'Current blueprint uses a different narrative thread',
                supportedValues: [currentState.blueprint.narrative_thread],
            },
        ];
    }

    return [];
}

/**
 * Format incompatibilities into a string suitable for AI instructions.
 *
 * @param incompatibilities - Array of incompatibilities to format
 * @returns Formatted string for AI additional_info, or empty string if no incompatibilities
 */
export function formatIncompatibilitiesForAI(incompatibilities: Incompatibility[]): string {
    if (incompatibilities.length === 0) {
        return '';
    }

    const lines = incompatibilities.map((inc) => {
        const supportedList =
            inc.supportedValues.length > 0
                ? `Use one of: ${inc.supportedValues.join(', ')}.`
                : 'This property should be removed.';

        return `- ${inc.property}: ${inc.reason}. ${supportedList}`;
    });

    return `The following properties are incompatible and need to be changed:\n${lines.join('\n')}`;
}

export function getCompatibleVideoPlanForBlueprint(
    currentVideoPlan: VideoPlan,
    currentTemplate: StoredTemplate,
    newBlueprint: Blueprint,
): VideoPlan {
    const videoPlanState = {
        template: currentTemplate,
        aspectRatio: currentVideoPlan.aspect_ratio,
        blueprint: null,
        narrativeThread: currentVideoPlan.narrative_thread,
        voiceSlug: currentVideoPlan.ai_voice,
    };
    const incompatabilies = getVideoPlanIncompatibilities(
        {
            type: 'blueprint',
            newValue: newBlueprint,
        },
        videoPlanState,
    );

    function _handleIncompatibility(property: IncompatibleProperty, videoPlan: VideoPlan) {
        switch (property) {
            case 'voice':
                videoPlan.ai_voice = null;
                break;
            case 'template':
                videoPlan.template_id = newBlueprint.default_template_id;
                break;
            case 'aspectRatio':
                videoPlan.aspect_ratio = newBlueprint.default_aspect_ratio;
                break;
            case 'narrativeThread':
                videoPlan.narrative_thread = newBlueprint.narrative_thread;
                break;
            case 'blueprint':
                break;
            default: {
                const _exhaustive: never = property;
                throw new Error(`Unhandled property: ${_exhaustive}`);
            }
        }
    }

    const updatedVideoPlan = { ...currentVideoPlan };
    for (const { property } of incompatabilies) {
        _handleIncompatibility(property, updatedVideoPlan);
    }

    return updatedVideoPlan;
}
